import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Season, Team } from '@/types'

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'skipped'
export type ThemePreference = 'light' | 'dark' | 'system' | 'high-contrast' | 'forest' | 'midnight' | 'graphite' | 'team' | 'forest-light' | 'midnight-light' | 'graphite-light' | 'team-light'

interface AppState {
  activeSeason: Season | null
  activeTeam: Team | null
  isOnline: boolean
  theme: ThemePreference
  // Sync state (syncStatus/syncError not persisted — reset on reload)
  syncStatus: SyncStatus
  lastSyncedAt: string | null
  syncError: string | null
  setActiveSeason: (season: Season | null) => void
  setActiveTeam: (team: Team | null) => void
  setOnline: (online: boolean) => void
  setTheme: (theme: ThemePreference) => void
  setSyncStatus: (status: SyncStatus) => void
  setLastSyncedAt: (t: string | null) => void
  setSyncError: (e: string | null) => void
  notificationsEnabled: boolean
  setNotificationsEnabled: (v: boolean) => void
  newTeamWizard: boolean  // transient — not persisted; resets each session
  setNewTeamWizard: (v: boolean) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      activeSeason: null,
      activeTeam: null,
      isOnline: navigator.onLine,
      theme: 'light',
      syncStatus: 'idle',
      lastSyncedAt: null,
      syncError: null,
      setActiveSeason: (activeSeason) => set({ activeSeason }),
      setActiveTeam: (activeTeam) => set({ activeTeam }),
      setOnline: (isOnline) => set({ isOnline }),
      setTheme: (theme) => set({ theme }),
      setSyncStatus: (syncStatus) => set({ syncStatus }),
      setLastSyncedAt: (lastSyncedAt) => set({ lastSyncedAt }),
      setSyncError: (syncError) => set({ syncError }),
      notificationsEnabled: false,
      setNotificationsEnabled: (notificationsEnabled) => set({ notificationsEnabled }),
      newTeamWizard: false,
      setNewTeamWizard: (newTeamWizard) => set({ newTeamWizard }),
    }),
    {
      name: 'whistle-app',
      partialize: (state) => ({
        activeSeason: state.activeSeason,
        activeTeam: state.activeTeam,
        lastSyncedAt: state.lastSyncedAt,
        theme: state.theme,
        notificationsEnabled: state.notificationsEnabled,
      }),
    }
  )
)
